import FieldFactory from './factories/field-factory.js';
import PromiseReducer from './helpers/promise-reducer.js';
import Tooltip from '../utils/tooltip.js';

export default class Form {
    constructor(el, callbacks) {
        this.el = el;
        this.callbacks = {
            onInit: callbacks.onInit ? [callbacks.onInit] : [],
            onSubmit: callbacks.onSubmit ? [callbacks.onSubmit] : [],
            onResult: callbacks.onResult ? [callbacks.onResult] : [],
            onSuccess: callbacks.onSuccess ? [callbacks.onSuccess] : [],
            onError: callbacks.onError ? [callbacks.onError] : [],
            onUpdate: callbacks.onUpdate ? [callbacks.onUpdate] : [],
            onInvalid: callbacks.onInvalid ? [callbacks.onInvalid] : [],
            action: callbacks.action,
        };
        this.form = this.el.querySelector('form');
        this.errorMessage = this.el.querySelector('.form_error_message');
        this.successMessage = this.el.querySelector('.form_success_message');
        this.button = this.el.querySelector('.form_submit button');
        this.loader = this.el.querySelector('.form_loader');

        this.fields = [...this.el.querySelectorAll('.form_field_composite, .form_field')]
            .filter((f) => !f.parentElement.closest('.form_field_composite'))
            .map((f) => FieldFactory.build(this, f))
            .filter((f) => f);

        this.bind();
        this.init();
        Tooltip.init();
    }

    init() {
        this.toggleButton();
        this.onInit();
    }

    field(id) {
        return this.fields.find((f) => id === f.id());
    }

    fieldBySelector(selector) {
        return this.fields.find((f) => f.el.matches(selector));
    }

    getFields(filter) {
        return this.fields.filter((f) => filter(f));
    }

    elementFields(element) {
        return this.fields.filter((f) => element.contains(f.el));
    }

    disable(arrOrEl) {
        const fields = arrOrEl instanceof HTMLElement ? this.elementFields(arrOrEl) : arrOrEl;
        fields.forEach((f) => f.disable());
    }

    enable(arrOrEl) {
        const fields = arrOrEl instanceof HTMLElement ? this.elementFields(arrOrEl) : arrOrEl;
        fields.forEach((f) => f.enable());
    }

    bind() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.valid().then(this.submit.bind(this));
        });
    }

    addEventListener(type, cb) {
        this.callbacks[type]?.push(cb);
    }

    async submit(valid, cbs) {
        if (valid) {
            this.showLoader();
            this.onSubmit(valid, cbs?.onSubmit);

            const body = new FormData(this.form);

            if (this.callbacks.action) {
                try {
                    const result = this.callbacks.action();
                    this.onResult(result, cbs?.onResult, body);
                    result.success ? this.onSuccess(result, cbs?.onSuccess, body) : this.onError(result, cbs?.onError);
                } catch (error) {
                    console.error(error);
                    this.hideLoader();
                    this.onError(error, cbs?.onError);
                }
            } else if (this.form.action) {
                try {
                    const response = await fetch(this.form.action, {
                        method: this.form.method || 'POST',
                        credentials: 'include',
                        body,
                    });

                    const result = await response.json();
                    this.onResult(result, cbs?.onResult, body);
                    this.hideLoader();
                    result.success ? this.onSuccess(result, cbs?.onSuccess, body) : this.onError(result, cbs?.onError);
                } catch (error) {
                    console.error(error);
                    this.hideLoader();
                    this.onError(error, cbs?.onError);
                }
            }
        } else {
            this.onInvalid();
            this.focusOnFirstInvalidElement();
            this.hideLoader();
        }
    }

    showSuccess() {
        if (this.successMessage) {
            this.successMessage.classList.remove('hide');
            this.successMessage.focus();
        }
    }

    hideSuccess() {
        this.successMessage?.classList.add('hide');
    }

    showError() {
        if (this.errorMessage) {
            this.errorMessage.classList.remove('hide');
            this.errorMessage.focus();
        }
    }

    hideError() {
        this.errorMessage.classList.add('hide');
    }

    showLoader() {
        this.loader?.classList.remove('hide');
    }

    hideLoader() {
        this.loader?.classList.add('hide');
    }

    async focusOnFirstInvalidElement() {
        for (const field of this.fields) {
            const isValid = await field.valid(true);

            if (isValid) {
                continue;
            }

            await field.focusOnFirstInvalid();
            break;
        }
    }

    update(field, valid) {
        this.onUpdate(field, valid);
        this.toggleButton();
        this.hideError();
    }

    onInit(cb) {
        this.callbacks.onInit.forEach((cb) => cb());
        cb?.();
    }

    onSubmit(valid, cb) {
        this.callbacks.onSubmit.forEach((cb) => cb());
        cb?.();
    }

    onResult(result, cb, body) {
        this.callbacks.onResult.forEach((cb) => cb(result, body));
        cb?.(result);
    }

    onSuccess(result, cb, body) {
        this.callbacks.onSuccess.forEach((cb) => cb(result, body));
        cb?.(result);
    }

    onError(error, cb) {
        this.callbacks.onError.forEach((cb) => cb(error));
        cb?.(result);
    }

    onUpdate(field, valid) {
        this.callbacks.onUpdate.forEach((cb) => cb(field, valid));
    }

    onInvalid() {
        this.callbacks.onInvalid.forEach((cb) => cb());
    }

    toggleButton() {
        const recaptchaField = this.el.querySelector('.form_field_recaptcha');
        const recaptchaValid = recaptchaField ? recaptchaField.dataset.captcha === 'true' : true;
        this.populated().then((p) =>
            p && recaptchaValid ? this.button.classList.remove('disabled') : this.button.classList.add('disabled'),
        );
    }

    valid(fields, silent) {
        return PromiseReducer.all((fields ?? this.fields).map((f) => f.valid(silent)));
    }

    populated(fields) {
        return PromiseReducer.all((fields ?? this.fields).map((f) => f.populated()));
    }
}
